import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { reportsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Area,
  AreaChart
} from 'recharts';
import { 
  BarChart3,
  ClipboardCheck,
  AlertTriangle,
  Package,
  Wrench,
  Users,
  TrendingUp,
  TrendingDown,
  Calendar,
  Loader2,
  RefreshCw,
  Download,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle
} from 'lucide-react';
import { Segmented } from '../components/ui/segmented';
import { toast } from 'sonner';
import {
  RC, SEVERITY_COLORS, StatCard as RStatCard, ChartEmpty, Panel, ColumnChart, RankBars, StackedBar,
  humanizeCategory, overduePill,
} from './reports/reportKit';

// Color palette
const COLORS = {
  primary: '#facc15',
  success: '#22c55e',
  warning: '#f97316',
  danger: '#ef4444',
  info: '#3b82f6',
  muted: '#6b7280',
  purple: '#a855f7',
  cyan: '#06b6d4'
};

const PIE_COLORS = [COLORS.success, COLORS.warning, COLORS.danger, COLORS.info, COLORS.purple];

// Time range options
const TIME_RANGES = [
  { value: '7', label: 'Last 7 Days' },
  { value: '14', label: 'Last 14 Days' },
  { value: '30', label: 'Last 30 Days' },
  { value: '60', label: 'Last 60 Days' },
  { value: '90', label: 'Last 90 Days' },
];

// Stat Card Component
const StatCard = ({ title, value, subtitle, icon: Icon, trend, trendValue, color = 'primary' }) => {
  const colorClasses = {
    primary: 'bg-primary/10 text-primary',
    success: 'bg-green-900/30 text-green-400',
    warning: 'bg-yellow-900/30 text-yellow-400',
    danger: 'bg-red-900/30 text-red-400',
    info: 'bg-blue-900/30 text-blue-400'
  };

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-3xl font-bold mt-1">{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
            {trend && (
              <div className={`flex items-center gap-1 mt-2 text-sm ${trend === 'up' ? 'text-green-400' : 'text-red-400'}`}>
                {trend === 'up' ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                <span>{trendValue}</span>
              </div>
            )}
          </div>
          <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Custom Tooltip for Charts
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
        <p className="font-medium text-sm mb-1">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// ===================== INSPECTION COMPLIANCE TAB =====================
const InspectionComplianceTab = ({ days }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { formatDate } = useSettings();
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, [days]);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await reportsApi.getInspectionCompliance(days);
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load inspection compliance data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!data) return null;

  const { summary, trend, by_category, overdue_assets, overdue_total } = data;
  const hasInsp = summary.total_inspections > 0;
  const catRows = (by_category || []).map(c => ({
    label: humanizeCategory(c.category),
    display: String(c.total ?? (c.passed + c.failed)),
    segments: [
      { value: c.passed || 0, color: RC.success },
      { value: c.failed || 0, color: RC.danger },
    ],
  }));
  const overdueShown = (overdue_assets || []).slice(0, 8);
  const moreCount = (overdue_total ?? (overdue_assets || []).length) - overdueShown.length;

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <RStatCard title="Inspections submitted" value={summary.total_inspections} subtitle={`Last ${days} days`} accent={RC.volt} testId="stat-inspections" />
        <RStatCard title="Pass rate" value={hasInsp ? `${summary.pass_rate}%` : '—'} subtitle={hasInsp ? `${summary.passed} of ${summary.total_inspections} passed` : 'No inspections to rate'} accent={RC.success} testId="stat-passrate" />
        <RStatCard title="Failed" value={summary.failed ?? 0} subtitle={hasInsp ? `Raised ${(data.defects_raised ?? summary.failed) || 0} defects` : 'None this period'} accent={RC.danger} testId="stat-failed" />
        <RStatCard title="Assets overdue" value={overdue_total ?? summary.assets_overdue ?? 0} subtitle={`Of ${data.total_assets ?? '—'} assets`} accent={RC.volt} testId="stat-overdue" />
      </div>

      {/* Charts Row */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Panel title="Inspection trend" subtitle="Weekly pass / fail" testId="panel-insp-trend">
          <ColumnChart
            data={trend}
            xKey="week"
            bars={[{ key: 'passed', name: 'Passed', color: RC.success }, { key: 'failed', name: 'Failed', color: RC.danger }]}
            emptyLine2="No inspections were submitted in the selected period."
            testId="insp-trend"
          />
        </Panel>

        <Panel title="By category" subtitle="Results per asset category" testId="panel-insp-category">
          {catRows.length === 0
            ? <ChartEmpty line2="Categories appear once inspections are recorded against assets." testId="insp-category-empty" />
            : <RankBars rows={catRows} testId="insp-category" />}
        </Panel>
      </div>

      {/* Assets Without Recent Inspection */}
      <Panel
        title="Assets without a recent inspection"
        subtitle={`${overdue_total ?? (overdue_assets || []).length} of ${data.total_assets ?? '—'} assets`}
        right={<Button variant="outline" size="sm" onClick={() => navigate('/maintenance')} data-testid="schedule-inspections-btn">Schedule inspections</Button>}
        testId="panel-overdue-assets"
      >
        {overdueShown.length === 0 ? (
          <ChartEmpty line2="Every asset has been inspected within this period." testId="overdue-assets-empty" />
        ) : (
          <div>
            <div className="grid items-center px-2 pb-2 border-b border-[#E6E2D9] text-[10px] font-bold uppercase tracking-[0.09em] text-[#8A857A]" style={{ gridTemplateColumns: 'minmax(200px,2fr) 140px 132px 120px' }}>
              <span>Asset</span><span>Category</span><span>Last inspected</span><span className="text-right">Overdue by</span>
            </div>
            {overdueShown.map((a) => {
              const pill = overduePill(a.overdue_days ?? 0);
              return (
                <div key={a.id} className="grid items-center px-2 border-b border-[#F4F2ED]" style={{ gridTemplateColumns: 'minmax(200px,2fr) 140px 132px 120px', padding: '10px 8px' }} data-testid={`overdue-asset-${a.id}`}>
                  <span className="text-[13.5px] text-[#1A1A1A] truncate pr-2">{a.name}</span>
                  <span className="text-[12.5px] text-[#6B6660]">{humanizeCategory(a.category)}</span>
                  <span className="text-[12px] font-mono text-[#6B6660]">{a.last_inspected ? formatDate(a.last_inspected) : 'Never'}</span>
                  <span className="text-right">
                    <span className="inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full border" style={{ color: pill.color, background: pill.bg, borderColor: pill.border }}>
                      {a.overdue_days != null ? `${a.overdue_days} days` : '—'}
                    </span>
                  </span>
                </div>
              );
            })}
            {moreCount > 0 && (
              <p className="text-[12px] text-[#8F8A7F] px-2 pt-2.5">Showing the 8 longest overdue · {moreCount} more</p>
            )}
          </div>
        )}
      </Panel>
    </div>
  );
};

// ===================== DEFECT SUMMARY TAB =====================
const DefectSummaryTab = ({ days }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [days]);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await reportsApi.getDefectSummary(days);
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load defect summary data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!data) return null;

  const { summary, by_severity, by_status, aging, trend, top_assets } = data;
  const critical = summary.critical ?? (by_severity || []).find(s => s.severity === 'critical')?.count ?? 0;
  const totalOpen = summary.total_open ?? 0;
  const sevOrder = { critical: 0, high: 1, medium: 2, low: 3 };
  const sevRows = [...(by_severity || [])]
    .sort((a, b) => (sevOrder[a.severity] ?? 9) - (sevOrder[b.severity] ?? 9))
    .map(s => ({ label: s.severity.charAt(0).toUpperCase() + s.severity.slice(1), value: s.count, color: SEVERITY_COLORS[s.severity] || RC.neutral }));
  const statusColor = { open: RC.warning, in_progress: RC.volt, resolved: RC.success };
  const statusSegs = (by_status || []).map(s => ({ label: s.status.replace(/_/g, ' ').replace(/^\w/, m => m.toUpperCase()), value: s.count, color: statusColor[s.status] || RC.neutral }));
  const agingRows = (aging || []).map(a => ({ label: a.bucket, value: a.count, color: /30/.test(a.bucket) ? RC.danger : RC.warning }));
  const topRows = (top_assets || []).map((a, i) => ({ label: `#${i + 1}  ${a.name}`, value: a.count, color: RC.danger, display: `${a.count} ${a.count === 1 ? 'defect' : 'defects'}` }));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <RStatCard title="Open defects" value={totalOpen} subtitle="Awaiting resolution" accent={RC.warning} testId="stat-open-defects" />
        <RStatCard title="Critical" value={critical} subtitle={totalOpen > 0 ? `${Math.round((critical / totalOpen) * 100)}% of open defects` : 'None open'} accent={RC.danger} testId="stat-critical" />
        <RStatCard title="Raised this period" value={summary.created_this_period ?? 0} subtitle={`Last ${days} days`} accent={RC.orange} testId="stat-raised" />
        <RStatCard title="Resolved this period" value={summary.resolved_this_period ?? 0} subtitle={`Last ${days} days`} accent={RC.success} testId="stat-resolved" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Panel title="By severity" testId="panel-severity">
          <RankBars rows={sevRows} emptyLine2="No open defects to break down." labelWidth={90} testId="def-severity" />
        </Panel>
        <Panel title="By status" testId="panel-status">
          {statusSegs.length === 0 ? <ChartEmpty line2="No defects in this period." /> : <StackedBar segments={statusSegs} testId="def-status" />}
        </Panel>
        <Panel title="Age of open defects" testId="panel-aging">
          <RankBars rows={agingRows} emptyLine2="No open defects to age." labelWidth={90} testId="def-aging" />
        </Panel>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Panel title="Weekly trend" subtitle="Created vs resolved" testId="panel-def-trend">
          <ColumnChart data={trend} xKey="week"
            bars={[{ key: 'created', name: 'Created', color: RC.orange }, { key: 'resolved', name: 'Resolved', color: RC.success }]}
            emptyLine2="No defects were created or resolved in this period." testId="def-trend" />
        </Panel>
        <Panel title="Assets with most defects" subtitle="Open defects only" testId="panel-top-assets">
          {topRows.length === 0 ? <ChartEmpty line2="No open defects on any asset." /> : <RankBars rows={topRows} labelWidth={180} testId="def-top-assets" />}
        </Panel>
      </div>
    </div>
  );
};

// ===================== ASSET HEALTH TAB =====================
const AssetHealthTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { formatDate } = useSettings();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await reportsApi.getAssetHealth();
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load asset health data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!data) return null;

  const { summary, by_status, by_category, problem_assets, upcoming_maintenance, utilization } = data;
  const total = summary.total_assets || 0;
  const fleetSegs = [
    { label: 'Active', value: summary.active || 0, color: RC.success },
    { label: 'In maintenance', value: summary.in_maintenance || 0, color: RC.warning },
    { label: 'Out of service', value: summary.out_of_service || 0, color: RC.danger },
  ];
  const catRows = (by_category || []).map(c => ({ label: humanizeCategory(c.category), value: c.count, color: RC.volt }));
  const utilRows = (utilization || []).map(u => ({ label: u.name, value: u.hours, color: RC.volt, display: u.hours >= 1000 ? `${(u.hours / 1000).toFixed(1)}k h` : `${u.hours} h` }));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <RStatCard title="Total assets" value={total} subtitle={`Across ${(by_category || []).length} categories`} accent={RC.volt} testId="stat-total-assets" />
        <RStatCard title="Active" value={summary.active ?? 0} subtitle={total > 0 ? `${Math.round((summary.active / total) * 100)}% of the fleet` : 'No assets'} accent={RC.success} testId="stat-active" />
        <RStatCard title="In maintenance" value={summary.in_maintenance ?? 0} subtitle={(summary.in_maintenance ?? 0) === 0 ? 'None off the road' : 'Currently down'} accent={RC.warning} testId="stat-in-maint" />
        <RStatCard title="Out of service" value={summary.out_of_service ?? 0} subtitle={(summary.out_of_service ?? 0) === 0 ? 'All operational' : 'Needs attention'} accent={RC.danger} testId="stat-oos" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Panel title="Fleet status" testId="panel-fleet-status">
          <StackedBar segments={fleetSegs} testId="asset-fleet-status" />
        </Panel>
        <Panel title="Fleet by category" subtitle="Assets per category" testId="panel-fleet-category">
          <RankBars rows={catRows} emptyLine2="No assets recorded yet." testId="asset-category" />
        </Panel>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Panel title="Assets requiring attention" subtitle="Open defects and failed inspections" testId="panel-problem-assets">
          {(problem_assets || []).length === 0 ? (
            <ChartEmpty line2="No assets have open defects or failed inspections." />
          ) : (
            <div>
              {problem_assets.map(a => (
                <div key={a.id} className="flex items-center justify-between border-b border-[#F4F2ED]" style={{ padding: '10px 8px' }} data-testid={`problem-asset-${a.id}`}>
                  <div className="min-w-0">
                    <div className="text-[13.5px] text-[#1A1A1A] truncate">{a.name}</div>
                    {a.unit_number && <div className="text-[11px] font-mono text-[#8F8A7F]">{a.unit_number}</div>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {a.defects > 0 && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full border" style={{ color: '#B91C1C', background: '#FEF2F2', borderColor: '#FECACA' }}>{a.defects} defects</span>}
                    {a.failed_inspections > 0 && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full border" style={{ color: '#8A6D00', background: '#FFF8D6', borderColor: '#EFE0A0' }}>{a.failed_inspections} failed</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>
        <Panel title="Hours utilisation" subtitle="Top 10 assets by operating hours" testId="panel-utilisation">
          <RankBars rows={utilRows} emptyLine2="No operating hours recorded." labelWidth={120} testId="asset-utilisation" />
        </Panel>
      </div>

      <Panel title="Upcoming maintenance" subtitle="Next 7 days" testId="panel-upcoming">
        {(upcoming_maintenance || []).length === 0 ? (
          <ChartEmpty line2="No maintenance is scheduled in the next 7 days." />
        ) : (
          <div>
            {upcoming_maintenance.map(m => (
              <div key={m.id} className="flex items-center justify-between border-b border-[#F4F2ED]" style={{ padding: '10px 8px' }} data-testid={`upcoming-${m.id}`}>
                <div className="min-w-0">
                  <span className="text-[12px] font-mono text-[#8F8A7F] mr-3">{formatDate(m.due_date)}</span>
                  <span className="text-[13.5px] text-[#1A1A1A]">{m.asset_name}</span>
                  <span className="text-[12.5px] text-[#6B6660]"> — {m.task_name}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
};

// ===================== WORK ORDER STATUS TAB =====================
const WorkOrderStatusTab = ({ days }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { formatDate } = useSettings();

  useEffect(() => {
    loadData();
  }, [days]);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await reportsApi.getWorkOrderStatus(days);
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load work order data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!data) return null;

  const { summary, by_status, trend, technician_workload, overdue } = data;

  const statusColorWO = { open: RC.neutral, in_progress: RC.volt, completed: RC.success };
  const woStatusSegs = (by_status || []).map(s => ({ label: s.status.replace(/_/g, ' ').replace(/^\w/, m => m.toUpperCase()), value: s.count, color: statusColorWO[s.status] || RC.neutral }));
  const hasAvg = summary.avg_completion_days != null && summary.avg_completion_days > 0;
  const techRows = (technician_workload || []).map(t => ({
    label: t.name,
    segments: [{ value: t.open || 0, color: RC.volt }, { value: t.completed || 0, color: RC.success }],
    display: `${(t.open || 0) + (t.completed || 0)}`,
  }));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <RStatCard title="Total work orders" value={summary.total ?? 0} subtitle={`Last ${days} days`} accent={RC.volt} testId="stat-wo-total" />
        <RStatCard title="Completion rate" value={summary.total > 0 ? `${summary.completion_rate}%` : '—'} subtitle={summary.total > 0 ? `${summary.completed} completed` : 'No work orders'} accent={RC.success} testId="stat-wo-completion" />
        <RStatCard title="Median time to close" value={hasAvg ? `${summary.avg_completion_days} days` : '—'} subtitle={hasAvg ? 'From open to done' : 'No completed WOs yet'} accent={RC.volt} testId="stat-wo-time" />
        <RStatCard title="Overdue" value={summary.overdue_count ?? 0} subtitle={(summary.overdue_count ?? 0) === 0 ? 'All on schedule' : 'Past due date'} accent={RC.danger} testId="stat-wo-overdue" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Panel title="Status distribution" testId="panel-wo-status">
          {woStatusSegs.length === 0 ? <ChartEmpty line2="No work orders in this period." /> : <StackedBar segments={woStatusSegs} testId="wo-status" />}
        </Panel>
        <Panel title="Weekly trend" subtitle="Created vs completed" testId="panel-wo-trend">
          <ColumnChart data={trend} xKey="week"
            bars={[{ key: 'created', name: 'Created', color: RC.orange }, { key: 'completed', name: 'Completed', color: RC.success }]}
            emptyLine2="No work orders were created or completed in this period." testId="wo-trend" />
        </Panel>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Panel title="Technician workload" subtitle="Open vs completed" testId="panel-tech-workload"
          right={<span className="inline-flex items-center gap-3 text-[11px] text-[#3D3A35]"><span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-[2px]" style={{ background: RC.volt }} />Open</span><span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-[2px]" style={{ background: RC.success }} />Completed</span></span>}>
          <RankBars rows={techRows} emptyLine2="No work orders assigned to technicians." testId="wo-tech" />
        </Panel>
        <Panel title="Overdue work orders" testId="panel-wo-overdue">
          {(overdue || []).length === 0 ? (
            <ChartEmpty line2="No work orders are past their due date." />
          ) : (
            <div className="max-h-[300px] overflow-y-auto">
              {overdue.map(wo => {
                const pill = overduePill(wo.days_overdue ?? 0);
                return (
                  <div key={wo.id} className="flex items-center justify-between border-b border-[#F4F2ED]" style={{ padding: '10px 8px' }} data-testid={`wo-overdue-${wo.id}`}>
                    <div className="min-w-0">
                      <p className="text-[13.5px] text-[#1A1A1A] truncate">{wo.title}</p>
                      <p className="text-[12px] text-[#8F8A7F] truncate">{wo.asset_name}</p>
                    </div>
                    <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full border shrink-0" style={{ color: pill.color, background: pill.bg, borderColor: pill.border }}>{wo.days_overdue} days</span>
                  </div>
                );
              })}
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
};

// ===================== TEAM ACTIVITY TAB =====================
const TeamActivityTab = ({ days }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [days]);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await reportsApi.getTeamActivity(days);
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load team activity data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!data) return null;

  const { summary, by_user, daily_trend } = data;
  const roleLabel = (r) => (r || '').replace(/_/g, ' ').replace(/^\w/, m => m.toUpperCase());

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <RStatCard title="Inspections" value={summary.total_inspections ?? 0} subtitle={`Last ${days} days`} accent={RC.volt} testId="stat-team-insp" />
        <RStatCard title="Defects reported" value={summary.total_defects_reported ?? 0} subtitle={`Last ${days} days`} accent={RC.warning} testId="stat-team-defects" />
        <RStatCard title="Work orders completed" value={summary.total_work_orders_completed ?? 0} subtitle={`Last ${days} days`} accent={RC.success} testId="stat-team-wo" />
        <RStatCard title="Active users" value={summary.active_users ?? 0} subtitle="With activity this period" accent={RC.volt} testId="stat-team-users" />
      </div>

      <Panel title="Daily activity" subtitle="Inspections and defects, last 14 days" testId="panel-daily">
        <ColumnChart data={daily_trend} xKey="date"
          bars={[{ key: 'inspections', name: 'Inspections', color: RC.volt }, { key: 'defects', name: 'Defects', color: RC.danger }]}
          emptyLine2="No inspections or defects were logged in the last 14 days." testId="team-daily" />
      </Panel>

      <Panel title="Activity by team member" testId="panel-by-user">
        {(by_user || []).length === 0 ? (
          <ChartEmpty line2="No team activity in this period." />
        ) : (
          <div className="overflow-x-auto">
            <div className="grid items-center px-2 pb-2 border-b border-[#E6E2D9] text-[10px] font-bold uppercase tracking-[0.09em] text-[#8A857A]" style={{ gridTemplateColumns: 'minmax(200px,2fr) 120px 90px 110px 90px 70px' }}>
              <span>Team member</span><span>Role</span><span className="text-center">Insp.</span><span className="text-center">Defects</span><span className="text-center">Work orders</span><span className="text-right">Total</span>
            </div>
            {by_user.map(u => (
              <div key={u.user_id} className="grid items-center px-2 border-b border-[#F4F2ED]" style={{ gridTemplateColumns: 'minmax(200px,2fr) 120px 90px 110px 90px 70px', padding: '9px 8px' }} data-testid={`team-user-${u.user_id}`}>
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="w-7 h-7 rounded-full bg-[#FFD108] flex items-center justify-center shrink-0"><span className="text-black font-bold text-[12px]">{u.name?.charAt(0).toUpperCase()}</span></div>
                  <span className="text-[13.5px] text-[#1A1A1A] truncate">{u.name}</span>
                </div>
                <span className="text-[12px] text-[#6B6660]">{roleLabel(u.role)}</span>
                <span className="text-center font-mono text-[12.5px] text-[#3D3A35]">{u.inspections}</span>
                <span className="text-center font-mono text-[12.5px] text-[#3D3A35]">{u.defects_reported}</span>
                <span className="text-center font-mono text-[12.5px] text-[#3D3A35]">{u.work_orders_completed}</span>
                <span className="text-right font-mono text-[13px] font-bold text-[#1A1A1A]">{u.total_activities}</span>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
};

// ===================== MAIN REPORTS PAGE =====================
export const ReportsPage = () => {
  const [timeRange, setTimeRange] = useState('30');
  const [activeTab, setActiveTab] = useState('inspections');
  const [chrome, setChrome] = useState({ total_assets: null, critical: null, overdue: null });
  const { isAdmin, isMechanic, isOfficeUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [ah, ds, wo] = await Promise.all([
          reportsApi.getAssetHealth().catch(() => null),
          reportsApi.getDefectSummary(timeRange).catch(() => null),
          reportsApi.getWorkOrderStatus(timeRange).catch(() => null),
        ]);
        if (cancelled) return;
        setChrome({
          total_assets: ah?.data?.summary?.total_assets ?? null,
          critical: ds?.data?.summary?.critical ?? ds?.data?.by_severity?.find?.(s => s.severity === 'critical')?.count ?? null,
          overdue: wo?.data?.summary?.overdue_count ?? null,
        });
      } catch { /* chrome is best-effort */ }
    })();
    return () => { cancelled = true; };
  }, [timeRange]);

  // Only admin and mechanic can view reports
  if (!isAdmin() && !isMechanic() && !isOfficeUser()) {
    navigate('/dashboard');
    return null;
  }

  const PERIODS = ['7', '14', '30', '60', '90'];

  const exportCsv = async () => {
    const fetchers = {
      inspections: () => reportsApi.getInspectionCompliance(timeRange),
      defects: () => reportsApi.getDefectSummary(timeRange),
      assets: () => reportsApi.getAssetHealth(),
      workorders: () => reportsApi.getWorkOrderStatus(timeRange),
      team: () => reportsApi.getTeamActivity(timeRange),
    };
    const primary = { inspections: 'overdue_assets', defects: 'top_assets', assets: 'problem_assets', workorders: 'overdue', team: 'by_user' };
    try {
      toast.info('Preparing export…');
      const res = await fetchers[activeTab]();
      const d = res.data || {};
      const rows = [];
      if (d.summary) { rows.push(['Summary']); Object.entries(d.summary).forEach(([k, v]) => rows.push([k, v])); rows.push([]); }
      const arr = d[primary[activeTab]] || [];
      if (arr.length) { rows.push(Object.keys(arr[0])); arr.forEach(o => rows.push(Object.values(o))); }
      if (rows.length === 0) { toast.error('Nothing to export for this tab'); return; }
      const csv = rows.map(r => r.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `reports-${activeTab}-${timeRange}d-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch {
      toast.error('Could not export this report');
    }
  };

  const tabDef = [
    { id: 'inspections', label: 'Inspection compliance' },
    { id: 'defects', label: 'Defect summary', badge: chrome.critical, tone: 'danger' },
    { id: 'assets', label: 'Asset health' },
    { id: 'workorders', label: 'Work orders', badge: chrome.overdue, tone: 'warning' },
    { id: 'team', label: 'Team activity' },
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-[20px] font-extrabold uppercase tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Reports</h1>
          <p className="text-muted-foreground" data-testid="reports-subtitle">
            Fleet analytics for the last {timeRange} days{chrome.total_assets != null ? ` · ${chrome.total_assets} assets` : ''}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-[#6B6660]">Period</span>
            <Segmented
              value={timeRange}
              onChange={setTimeRange}
              testId="reports-period"
              options={PERIODS.map(p => ({ id: p, label: `${p}d`, testId: `period-${p}` }))}
            />
          </div>
          <Button variant="outline" className="h-[34px] border-[#C9C3B6] rounded-[7px]" onClick={exportCsv} data-testid="reports-export">
            <Download className="w-4 h-4 mr-2" /> Export
          </Button>
        </div>
      </div>

      {/* Report Tabs — same Segmented control as the Period pills */}
      <div>
        <Segmented
          value={activeTab}
          onChange={setActiveTab}
          testId="reports-tabs"
          options={tabDef.map(t => ({ id: t.id, label: t.label, testId: `tab-${t.id}`, badge: t.badge, badgeTone: t.tone }))}
        />

        <div className="mt-6">
          {activeTab === 'inspections' && <InspectionComplianceTab days={parseInt(timeRange)} />}
          {activeTab === 'defects' && <DefectSummaryTab days={parseInt(timeRange)} />}
          {activeTab === 'assets' && <AssetHealthTab />}
          {activeTab === 'workorders' && <WorkOrderStatusTab days={parseInt(timeRange)} />}
          {activeTab === 'team' && <TeamActivityTab days={parseInt(timeRange)} />}
        </div>
      </div>
    </div>
  );
};

export default ReportsPage;
